import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OrderService } from '../../services';
import { handleNucleusResponse } from '../../services/utils';
import type {
  CreateKotRequest,
  CreateOrderRequest,
  OrderFilters,
  UpdateKotItemRequest,
  UpdateKotRequest,
  UpdateOrderRequest,
} from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';
import {
  deleteQueryItem,
  sanitizeFilters,
  updateQueryItem,
  upsertQueryItem,
} from '@billx/utils';

import { type QueryOptions } from '../types';

export const useOrders = (
  queryFilters: OrderFilters,
  options: QueryOptions
) => {
  const filters = sanitizeFilters(queryFilters);
  const queryClient = useQueryClient();

  const QUERY_KEY = ['orders', filters];

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (!Object.keys(filters).length) {
        throw new Error('No filters provided');
      }
      const response = await OrderService.fetchOrders(filters);
      return handleNucleusResponse(response).data;
    },
    enabled: !!Object.keys(filters).length && options.enabled,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
  });

  const createOrder = useMutation({
    mutationFn: (data: CreateOrderRequest) => OrderService.createOrder(data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, upsertQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateOrder = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrderRequest }) =>
      OrderService.updateOrder(id, data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const deleteOrder = useMutation({
    mutationFn: (id: string) => OrderService.deleteOrder(id),
    onSuccess: (response, id) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, deleteQueryItem(id));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  // KOT Mutations
  const createKot = useMutation({
    mutationFn: ({
      orderId,
      data,
    }: {
      orderId: string;
      data: CreateKotRequest;
    }) => OrderService.createKot(orderId, data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, upsertQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateKot = useMutation({
    mutationFn: ({
      orderId,
      kotId,
      data,
    }: {
      orderId: string;
      kotId: string;
      data: UpdateKotRequest;
    }) => OrderService.updateKot(orderId, kotId, data),
    onSuccess: (response, variables) => {
      if (response.success) {
        if (response.data) {
          // Order still exists, update it
          queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
        } else {
          // Order was deleted (e.g., single KOT rejected)
          queryClient.setQueryData(
            QUERY_KEY,
            deleteQueryItem(variables.orderId)
          );
        }
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateKotItem = useMutation({
    mutationFn: ({
      orderId,
      kotId,
      itemId,
      data,
    }: {
      orderId: string;
      kotId: string;
      itemId: string;
      data: UpdateKotItemRequest;
    }) => OrderService.updateKotItem(orderId, kotId, itemId, data),
    onSuccess: (response, variables) => {
      if (response.success) {
        if (response.data) {
          // Order still exists with remaining KOTs, update it
          queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
        } else {
          // Order was deleted (only had one KOT)
          queryClient.setQueryData(
            QUERY_KEY,
            deleteQueryItem(variables.orderId)
          );
        }
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const deleteKot = useMutation({
    mutationFn: ({ orderId, kotId }: { orderId: string; kotId: string }) =>
      OrderService.deleteKot(orderId, kotId),
    onSuccess: (response, variables) => {
      if (response.success) {
        if (response.data) {
          // Order still exists with remaining KOTs, update it
          queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
        } else {
          // Order was deleted (only had one KOT)
          queryClient.setQueryData(
            QUERY_KEY,
            deleteQueryItem(variables.orderId)
          );
        }
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const { data: orders = [], ...restQuery } = query;
  return {
    // Query data
    orders,
    ...restQuery,
    queryKey: QUERY_KEY,
    queryClient,

    // Order Mutations
    createOrder,
    updateOrder,
    deleteOrder,

    // KOT Mutations
    createKot,
    updateKot,
    updateKotItem,
    deleteKot,
  };
};
